import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';

interface SettingsContextType {
  systemName: string;
  systemLogo: string | undefined;
  isLoading: boolean;
  refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [systemName, setSystemName] = useState('Itororo SAC');
  const [systemLogo, setSystemLogo] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);

  const loadSettings = async () => {
    try {
      const settings = await api.getSettings();
      setSystemName(settings.systemName || 'Itororo SAC');
      setSystemLogo(settings.systemLogo);
    } catch (error) {
      console.error('Failed to load settings:', error);
      // Fallback to default
      setSystemName('Itororo SAC');
      setSystemLogo(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // Update favicon whenever systemLogo changes
  useEffect(() => {
    const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = systemLogo ? `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}${systemLogo}` : '/vite.svg';
    document.getElementsByTagName('head')[0].appendChild(link);
  }, [systemLogo]);

  const refreshSettings = async () => {
    setIsLoading(true);
    await loadSettings();
  };

  return (
    <SettingsContext.Provider value={{ systemName, systemLogo, isLoading, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
